import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, Loader2, FilterX, ChevronLeft, ChevronRight, Activity, User as UserIcon, ShieldCheck, X, Download } from "lucide-react";

const ACTION_COLORS = {
  login: { bg: "#E6F4EA", fg: "#1B5E20" },
  create: { bg: "#E3F2FD", fg: "#0D47A1" },
  update: { bg: "#FFF4E5", fg: "#8A4D0F" },
  delete: { bg: "#FDECEA", fg: "#B30000" },
  upload: { bg: "#F3E8FF", fg: "#6B21A8" },
  export: { bg: "#E0F2F1", fg: "#00695C" },
  import: { bg: "#E0F2F1", fg: "#00695C" },
  pdf_generate: { bg: "#E8EAF6", fg: "#283593" },
  xlsx_generate: { bg: "#E8EAF6", fg: "#283593" },
  magazzino: { bg: "#FFF8E1", fg: "#7A5D00" },
  report_generate: { bg: "#F1F8E9", fg: "#33691E" },
  unlock: { bg: "#FFEBEE", fg: "#C62828" },
  archive: { bg: "#DCFCE7", fg: "#065F46" },
  section_complete: { bg: "#DCFCE7", fg: "#065F46" },
  section_complete_all: { bg: "#DCFCE7", fg: "#065F46" },
  section_reopen: { bg: "#FEF3C7", fg: "#92400E" },
  section_reopen_all: { bg: "#FEF3C7", fg: "#92400E" },
  add: { bg: "#E0F2FE", fg: "#075985" },
  add_note: { bg: "#FEF9C3", fg: "#854D0E" },
  remove: { bg: "#FEE2E2", fg: "#991B1B" },
  mark_planned: { bg: "#F1F8E9", fg: "#33691E" },
  unmark_planned: { bg: "#FEF3C7", fg: "#92400E" },
  connect: { bg: "#F3E8FF", fg: "#6B21A8" },
  send: { bg: "#F3E8FF", fg: "#6B21A8" },
};

const ACTION_LABELS = {
  login: "Login", create: "Creazione", update: "Modifica", delete: "Eliminazione",
  upload: "Upload", export: "Export", import: "Import",
  pdf_generate: "PDF generato", xlsx_generate: "Excel generato",
  magazzino: "Movimento magazzino", report_generate: "Report", unlock: "Sblocco account",
  archive: "Archiviazione Aruba",
  section_complete: "Sezione completata",
  section_reopen: "Sezione riaperta",
  section_complete_all: "Tutte sezioni completate",
  section_reopen_all: "Tutte sezioni riaperte",
  add: "Aggiunto", add_note: "Nota aggiunta", remove: "Rimosso",
  mark_planned: "Pianificato", unmark_planned: "Pianificazione annullata",
  connect: "Connesso", send: "Inviato",
  nextcloud_create_commessa_folder: "Aruba: cartella commessa",
  nextcloud_sync: "Aruba: sync",
  nextcloud_create_folders: "Aruba: cartelle",
};

const RESOURCE_LABELS = {
  user: "Utente", cliente: "Cliente", clienti: "Clienti", commessa: "Commessa",
  prodotto: "Prodotto", file: "File", login: "Login", materiali: "Materiali",
  sopralluogo: "Sopralluogo", sopralluogo_section: "Sezione sopralluogo",
  installazione: "Installazione", installazione_section: "Sezione installazione",
  intervento: "Intervento", intervento_section: "Sezione intervento",
  shared_link: "Link pubblico", carico: "Carico magazzino", scarico: "Scarico magazzino",
  materiali_mensile: "Report mensile", settings: "Impostazioni",
  commessa_nextcloud: "Cartella Aruba commessa",
  pianificazione: "Pianificazione", pianificazione_move: "Spostamento",
  check_quality: "Check qualità",
  interventi_da_pianificare: "Intervento da pianificare",
  da_pianificare: "Installazione da pianificare",
  dico: "DiCo", dico_componente: "Componente DiCo",
  dico_impresa: "Impostazioni DiCo", dico_impresa_image: "Logo/Firma DiCo",
  integration_whatsapp_web: "WhatsApp", integration_telegram: "Telegram",
  integration_telegram_message: "Messaggio Telegram",
};

const PAGE_SIZE = 50;

function ActionBadge({ action }) {
  const c = ACTION_COLORS[action] || { bg: "#F3F4F6", fg: "#374151" };
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ background: c.bg, color: c.fg }} data-testid={`action-badge-${action}`}>
      {ACTION_LABELS[action] || action}
    </span>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [facets, setFacets] = useState({ actions: [], resource_types: [], users: [] });

  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [resourceType, setResourceType] = useState("");
  const [userId, setUserId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [clienteName, setClienteName] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Autocomplete cliente
  const [clienteQuery, setClienteQuery] = useState("");
  const [clienteOptions, setClienteOptions] = useState([]);
  const [clienteOpen, setClienteOpen] = useState(false);
  const clienteDebRef = useRef(null);

  const debounceRef = useRef(null);
  const firstLoad = useRef(true);

  const loadFacets = async () => {
    try {
      const { data } = await api.get("/audit-logs/facets");
      setFacets(data);
    } catch { /* noop */ }
  };

  const loadLogs = async (nextSkip = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (action) params.set("action", action);
      if (resourceType) params.set("resource_type", resourceType);
      if (userId) params.set("user_id", userId);
      if (clienteId) params.set("cliente_id", clienteId);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("skip", String(nextSkip));
      params.set("limit", String(PAGE_SIZE));
      const { data } = await api.get(`/audit-logs?${params}`);
      setItems(data.items);
      setTotal(data.total);
      setSkip(data.skip);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFacets();
    loadLogs(0);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadLogs(0), 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q, action, resourceType, userId, clienteId, dateFrom, dateTo]);

  // Autocomplete clienti
  useEffect(() => {
    if (clienteDebRef.current) clearTimeout(clienteDebRef.current);
    if (!clienteQuery || clienteQuery.length < 2) { setClienteOptions([]); return; }
    clienteDebRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clienti?q=${encodeURIComponent(clienteQuery)}&limit=10`);
        const list = Array.isArray(data) ? data : (data.items || []);
        setClienteOptions(list);
      } catch { setClienteOptions([]); }
    }, 250);
    return () => clearTimeout(clienteDebRef.current);
  }, [clienteQuery]);

  const selectCliente = (c) => {
    const display = c.ragione_sociale || `${c.cognome || ""} ${c.nome || ""}`.trim() || c.codice_cliente || c.id;
    setClienteId(c.id);
    setClienteName(display);
    setClienteQuery("");
    setClienteOptions([]);
    setClienteOpen(false);
  };
  const clearCliente = () => { setClienteId(""); setClienteName(""); setClienteQuery(""); setClienteOptions([]); };

  const hasFilters = !!(q || action || resourceType || userId || clienteId || dateFrom || dateTo);
  const clearAll = () => {
    setQ(""); setAction(""); setResourceType(""); setUserId(""); clearCliente(); setDateFrom(""); setDateTo("");
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (action) params.set("action", action);
      if (resourceType) params.set("resource_type", resourceType);
      if (userId) params.set("user_id", userId);
      if (clienteId) params.set("cliente_id", clienteId);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data, headers } = await api.get(`/audit-logs/export/csv?${params}`, { responseType: "blob" });
      // Estrai filename da Content-Disposition (se presente)
      const cd = headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `log_attivita_${Date.now()}.csv`;
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`CSV esportato · ${filename}`);
    } catch (err) {
      toast.error(`Errore durante l'export CSV: ${err?.response?.data?.detail || err.message || err}`);
    } finally {
      setExporting(false);
    }
  };

  const page = Math.floor(skip / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="audit-logs-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "var(--primary)" }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Audit &amp; Sicurezza
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Log attività</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }} data-testid="audit-count">
            {loading ? "…" : `${total.toLocaleString("it-IT")} eventi totali`}
            {hasFilters && <span> · <span style={{ color: "var(--accent)" }}>filtrati</span></span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={exporting || total === 0}
            className="btn-secondary text-sm inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="audit-export-csv"
            title={hasFilters ? "Esporta i log filtrati in CSV" : "Esporta tutti i log in CSV"}
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Esporta CSV
            {hasFilters && <span className="ml-1 text-[10px] font-normal opacity-75">({total.toLocaleString("it-IT")})</span>}
          </button>
        </div>
      </div>

      {clienteId && (
        <div className="card-shell p-3 mb-4 flex items-center justify-between" style={{ background: "var(--surface-muted)" }}>
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="w-4 h-4" style={{ color: "var(--primary)" }} />
            Storico attività di <b style={{ marginLeft: 6 }}>{clienteName}</b>
            <span className="text-xs ml-2" style={{ color: "var(--text-muted)" }}>({total} eventi)</span>
          </div>
          <button onClick={clearCliente} className="btn-secondary text-xs" data-testid="clear-cliente-filter"><X className="w-3.5 h-3.5" /> Rimuovi filtro cliente</button>
        </div>
      )}

      <div className="card-shell p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="relative md:col-span-3">
            <Search className="w-4 h-4 absolute left-3 top-3" style={{ color: "var(--text-muted)" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca nome cliente, azione, ID…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="audit-search"
            />
          </div>

          <div className="relative md:col-span-3">
            <input
              value={clienteId ? clienteName : clienteQuery}
              onChange={(e) => { if (clienteId) return; setClienteQuery(e.target.value); setClienteOpen(true); }}
              onFocus={() => setClienteOpen(true)}
              readOnly={!!clienteId}
              placeholder="🔎 Storico per cliente specifico"
              className="w-full pl-3 pr-8 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: clienteId ? "#F0FDF4" : "#fff" }}
              data-testid="filter-cliente-input"
            />
            {clienteId && (
              <button onClick={clearCliente} className="absolute right-2 top-2.5" data-testid="clear-cliente-input">
                <X className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
              </button>
            )}
            {clienteOpen && clienteOptions.length > 0 && !clienteId && (
              <div className="absolute top-full mt-1 left-0 right-0 z-20 max-h-56 overflow-auto rounded-md border shadow-lg bg-white"
                style={{ borderColor: "var(--border)" }}
                data-testid="cliente-autocomplete">
                {clienteOptions.map((c) => {
                  const label = c.ragione_sociale || `${c.cognome || ""} ${c.nome || ""}`.trim() || c.codice_cliente || c.id;
                  return (
                    <button key={c.id} onClick={() => selectCliente(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface-muted)]"
                      data-testid={`cliente-option-${c.id}`}>
                      <div className="font-semibold">{label}</div>
                      {c.codice_cliente && <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{c.codice_cliente}</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <select value={action} onChange={(e) => setAction(e.target.value)}
            className="md:col-span-2 px-3 py-2 text-sm rounded-md border outline-none"
            style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="filter-action">
            <option value="">Tutte le azioni</option>
            {facets.actions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>

          <select value={resourceType} onChange={(e) => setResourceType(e.target.value)}
            className="md:col-span-2 px-3 py-2 text-sm rounded-md border outline-none"
            style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="filter-resource-type">
            <option value="">Tutte le risorse</option>
            {facets.resource_types.map((r) => <option key={r} value={r}>{RESOURCE_LABELS[r] || r}</option>)}
          </select>

          <select value={userId} onChange={(e) => setUserId(e.target.value)}
            className="md:col-span-2 px-3 py-2 text-sm rounded-md border outline-none"
            style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="filter-user">
            <option value="">Tutti gli utenti</option>
            {facets.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>

          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="md:col-span-6 px-2 py-2 text-sm rounded-md border outline-none"
            style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="filter-date-from" title="Data da" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="md:col-span-6 px-2 py-2 text-sm rounded-md border outline-none"
            style={{ borderColor: "var(--border)", background: "#fff" }}
            data-testid="filter-date-to" title="Data a" />
        </div>

        {hasFilters && (
          <div className="flex justify-end mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
            <button onClick={clearAll} className="inline-flex items-center gap-1.5 text-xs font-semibold hover:underline"
              style={{ color: "var(--accent)" }} data-testid="clear-audit-filters">
              <FilterX className="w-3.5 h-3.5" /> Cancella filtri
            </button>
          </div>
        )}
      </div>

      <div className="card-shell overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Activity className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
            <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="audit-empty">
              {hasFilters ? "Nessun evento con i filtri attuali" : "Nessun log registrato"}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--surface-muted)" }}>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Data/Ora</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Utente</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Azione</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Cosa è stato fatto</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Cliente / Oggetto</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="border-t hover:bg-[var(--surface-muted)]" style={{ borderColor: "var(--border)" }} data-testid={`audit-row-${l.id}`}>
                    <td className="px-4 py-3 text-xs whitespace-nowrap font-mono" style={{ color: "var(--text-muted)" }}>{formatTs(l.timestamp)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                          style={{ background: l.user_id ? "var(--primary)" : "var(--text-muted)" }}>
                          {l.user_id ? <span>{(l.user_name || "U").slice(0, 1).toUpperCase()}</span> : <UserIcon className="w-3.5 h-3.5" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold truncate" data-testid={`audit-user-${l.id}`}>{l.user_name || "Sistema"}</div>
                          {l.user_email && <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{l.user_email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><ActionBadge action={l.action} /></td>
                    <td className="px-4 py-3 text-sm max-w-[420px]">
                      <div data-testid={`audit-description-${l.id}`}>{l.description || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-sm max-w-[240px]">
                      {l.cliente_id ? (
                        <button onClick={() => { setClienteId(l.cliente_id); setClienteName(l.resource_name || ""); }}
                          className="text-left hover:underline"
                          style={{ color: "var(--primary)" }}
                          title="Filtra tutte le attività di questo cliente"
                          data-testid={`audit-cliente-link-${l.id}`}>
                          <div className="font-semibold truncate">{l.resource_name || "—"}</div>
                        </button>
                      ) : (
                        <div className="truncate" data-testid={`audit-resource-${l.id}`}>{l.resource_name || "—"}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      {l.meta && Object.keys(l.meta).length > 0 ? (
                        <code className="font-mono text-[11px]">{JSON.stringify(l.meta).slice(0, 70)}{JSON.stringify(l.meta).length > 70 ? "…" : ""}</code>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="audit-pagination-info">
              Mostrati {items.length > 0 ? skip + 1 : 0}–{Math.min(skip + items.length, total)} di {total.toLocaleString("it-IT")}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => loadLogs(Math.max(0, skip - PAGE_SIZE))} disabled={skip === 0}
                className="btn-secondary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="audit-prev">
                <ChevronLeft className="w-3.5 h-3.5" /> Precedente
              </button>
              <span className="text-xs font-semibold" data-testid="audit-page-indicator">Pagina {page} / {pages}</span>
              <button onClick={() => loadLogs(skip + PAGE_SIZE)} disabled={skip + PAGE_SIZE >= total}
                className="btn-secondary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="audit-next">
                Successiva <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
